import _, { debounce } from 'lodash';
import React, { useMemo, useState, useCallback } from 'react';

import { Box } from '@mui/system';
import {
  Chip,
  Badge,
  Stack,
  Button,
  Select,
  Popover,
  Tooltip,
  MenuItem,
  useTheme,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  useMediaQuery,
  InputAdornment
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { EntityLabelType } from 'src/services/utils/utils.types';
import { useSearchLabelsQuery } from 'src/services/utils/utils.service';
import { CandidateAvailabilityStatus } from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import { TableProps } from 'src/components/table';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { useNavigate } from 'react-router';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import TableSorter from 'src/components/sorter/table-sorter';
import { SortOrder } from 'src/services/common.types';
import { SortConfig } from 'src/services/api.types';


export interface ToolbarOptions {
  addButton: boolean;
}

export type Props = {
  table?: TableProps;
  onFilters: (type: string, value: any) => void;
  onSortChange?: (sortConfig: SortConfig) => void;
  clearFilters: () => void;
  onInviteCandidates?: () => void;
  onCreateCandidate?: () => void;
  onImportCandidate?: () => void;
  onBulkAddToJob?: (candidateIds: string[]) => void;
  onAddToList?: (candidateIds: string[]) => void;
  onSendMultiEmail?: (candidateIds: string[]) => void;
  onBulkImport?: () => void;
};

export default function CandidatesTableToolbar({
  table,
  onFilters,
  onSortChange,
  clearFilters,
  onInviteCandidates,
  onCreateCandidate,
  onImportCandidate,
  onBulkAddToJob,
  onAddToList,
  onSendMultiEmail,
  onBulkImport
}: Props) {

  const [searchValue, setSearchValue] = useState('');
  const [skillsQuery, setSkillsQuery] = useState('');
  const [availability, setAvailability] = useState<CandidateAvailabilityStatus | "">("");
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<{ value: string, label: string }[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<{ value: string }[]>([]);

  const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

  const actionsControl = usePopover();

  const router = useRouter();

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelSearchQuery ?? undefined,
    type: EntityLabelType.CANDIDATE,
    page: 1,
    per_page: 10
  });

  const { canAccess } = useFeatures();

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tenantType = useOrgTenant();

  const onClearFilters = () => {
    setSearchValue('');
    setSelectedSkills([]);
    setAvailability("");
    setSelectedLabels([]);
    clearFilters();
  };

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('search_query', event.target.value);
      setSearchValue(event.target.value);
    },
    [onFilters]
  );

  const handleFilterLabels = useCallback(
    (event: React.SyntheticEvent, value: { label: string, value: string }[]) => {
      setSelectedLabels(value);
      onFilters('labels', value.map((label => label.value)));
    }, [onFilters]);

  const handleFilterAvailability = useCallback(
    (status: CandidateAvailabilityStatus) => {
      onFilters('availability_status', status);
      setAvailability(status);
    },
    [onFilters]
  );

  const handleOpenFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const addSkillFilter = useCallback(
    (skill: string) => {
      // if comma separated skills, split them and add each skill individually
      let newSkills = [];
      if (skill.includes(',')) {
        newSkills = skill.split(',').map((s) => s.trim()).filter((s) => s);
      }
      else {
        newSkills = [skill.trim()];
      }

      const allSkills = [...selectedSkills.map((s) => s.value), ...newSkills].map((s) => _.capitalize(s));
      const uniqueSkills = Array.from(new Set(allSkills)).map((s) => ({ value: s }));

      setSelectedSkills(uniqueSkills);
      onFilters('skills', uniqueSkills.map((s) => s.value));
    }, [onFilters, selectedSkills]);

  const removeSkillFilter = useCallback(
    (skill: string) => {
      const newSkills = selectedSkills.filter((s) => s.value !== skill);
      setSelectedSkills(newSkills);
      onFilters('skills', newSkills.map((s) => s.value));
    }, [onFilters, selectedSkills]);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setLabelSearchQuery(value);
    }, 500),
    [setLabelSearchQuery]
  );

  const selectableLabels = useMemo(() => {
    if (!availableLabels?.results) return [];

    return availableLabels.results.map((label) => ({
      label: label.label as string,
      value: label.id as string,
    }));
  }, [availableLabels]);

  const disableReset = useMemo(() => !searchValue && !availability && selectedSkills.length === 0 && selectedLabels.length === 0, [searchValue, availability, selectedSkills, selectedLabels]);
  const filterActiveCount = useMemo(() => {
    let count = 0;
    if (searchValue) count++;
    if (availability) count++;
    if (selectedSkills.length > 0) count++;
    if (selectedLabels.length > 0) count++;

    return count;
  }, [searchValue, availability, selectedSkills, selectedLabels]);

  const filterContent = (
    <Stack spacing={2} p={1} display="flex" direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center', alignContent: 'center', width: '100%' }} flexWrap="wrap">
      <Stack spacing={2} display="flex" direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}>
        <TextField
          onChange={handleFilterName}
          placeholder={t('common.placeholders.search')}
          value={searchValue}
          name="search"
          sx={{ width: { sm: '200px', xs: '100%' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        {
          tenantType === TenantType.Recruiter && (
            <FormControl sx={{ width: { sm: '200px', xs: '100%' } }}>
              <InputLabel>{t('candidates.filters.availability')}</InputLabel>
              <Select
                id="candidate-status-select"
                value={availability}
                sx={{
                  width: { sm: '200px', xs: '100%' }
                }}
                onChange={(event) => handleFilterAvailability(event.target.value as CandidateAvailabilityStatus)}
                label={t('candidates.filters.availability')}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {Object.values(CandidateAvailabilityStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(`enums.candidate_availability_status.${status}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }

        <TextField
          onKeyDown={(e) => {
            if (e.key === 'Enter' && skillsQuery) {
              addSkillFilter(skillsQuery);
              setSkillsQuery('');
            }
          }}
          onChange={(e) => setSkillsQuery(e.target.value)}
          placeholder={t('common.placeholders.search_skills')}
          value={skillsQuery}
          sx={{
            width: { sm: '200px', xs: '100%' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <Autocomplete
          multiple={true as boolean}
          options={selectableLabels}
          disableCloseOnSelect
          sx={{
            width: { sm: '200px', xs: '100%' }
          }}
          renderOption={({ key, ...rest }, option: any) => (
            <li {...rest} key={option.value}>
              <Stack direction="row" justifyContent="space-between" width="100%">
                <Typography variant="subtitle2">{option.label}</Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('candidates.filters.labels')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          value={selectedLabels}
          onInputChange={(_e, value) => onSearchQueryChange(value)}
          onChange={(event, value) => handleFilterLabels(event, value)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />

        {isMobile ? (
          <Button
            variant="text"
            disabled={disableReset}
            onClick={onClearFilters}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}
          >
            {t('common.clear')}

            {
              filterActiveCount > 0 && <Chip color="error" label={filterActiveCount} size='small' />
            }

          </Button>
        ) :
          <FormControl>
            <Tooltip title={t('common.clear')}>

              <IconButton
                size="large"
                disabled={disableReset}
                onClick={onClearFilters}
                aria-label={t('common.clear')}
              >
                <Badge invisible={!filterActiveCount} color="error" badgeContent={filterActiveCount}>
                  <Iconify icon="material-symbols:cancel" />
                </Badge>
              </IconButton>
            </Tooltip>
          </FormControl>
        }
      </Stack>
    </Stack >
  );

  const bulkAddDisabledTooltip = useMemo(() => {

    if (!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)) {
      return t('billing.feature_locked_tooltip');
    } if (!table?.selected.length) {
      return t('candidates.add_to_job_bulk_disabled');
    }

    return '';
  }, [canAccess, table?.selected.length, t]);

  const isBulkAddDisabled = useMemo(() => !canAccess(FeatureType.ADD_CANDIDATE_LIMIT) || !table?.selected.length, [canAccess, table?.selected.length]);

  const navigateToAdvancedSearch = () => {
    router.push(paths.dashboard.candidates.search);
  }

  return (
    <Stack
      spacing={2}
      display="flex"
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent={{ xs: 'center', sm: 'space-between' }}
      sx={{
        p: 2,
        width: '100%',
      }}
    >
      {isMobile ? (
        <>
          <Badge invisible={!filterActiveCount} color="error" badgeContent={filterActiveCount} sx={{ marginRight: 1 }} >
            <Button
              variant="outlined"
              onClick={handleOpenFilter}
              fullWidth
              startIcon={<Iconify icon="mdi:filter" />}
            >

              {t('common.filter')}
            </Button>
          </Badge>

          {[TenantType.Recruiter, TenantType.Client].includes(tenantType) && (
            <Tooltip title={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT) ? t('billing.feature_locked_tooltip') : ''} sx={{ cursor: 'pointer' }} >
              <span>
                <Button
                  size="medium"
                  onClick={actionsControl.onOpen}
                  startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
                  variant="outlined"
                  disabled={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)}
                  sx={{ textWrap: 'nowrap', width: '100%' }}
                >
                  {t('common.actions')}
                </Button>
              </span>
            </Tooltip>
          )}

          {
            selectedLabels.length > 0 && (
              <Stack direction="column" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('candidates.filters.selected_labels')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {selectedLabels.map((label) => (
                    <Chip
                      key={label.value}
                      label={label.label}
                      onDelete={() => {
                        const newLabels = selectedLabels.filter((l) => l.value !== label.value);
                        setSelectedLabels(newLabels);
                        onFilters('labels', newLabels.map((l) => l.value));
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )
          }

          {
            selectedSkills.length > 0 && (
              <Stack direction="column" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('candidates.filters.selected_skills')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {selectedSkills.map((label) => (
                    <Chip
                      key={label.value}
                      label={label.value}
                      onDelete={() => {
                        const newSkills = selectedSkills.filter((l) => l.value !== label.value);
                        setSelectedSkills(newSkills);
                        onFilters('skills', newSkills.map((l) => l.value));
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )
          }


          <Popover
            sx={{ padding: 2 }}
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleCloseFilter}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {filterContent}
          </Popover>
        </>
      ) : (
        <Stack direction="column" sx={{ width: '100%' }} spacing={1}>
          <Stack sx={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', 'width': '100%' }} gap={2}>
            {filterContent}

            <Box display="flex" alignContent="center" justifyContent="center" gap={2}>

              {
                onSortChange && (
                  <TableSorter
                    sortOptions={[
                      { key: 'name', label: t('candidates.sorters.name') },
                      { key: 'created_at', label: t('candidates.sorters.created_at') },
                      { key: 'updated_at', label: t('candidates.sorters.updated_at') },
                      { key: 'rating', label: t('candidates.sorters.rating') },
                      { key: 'availability_status', label: t('candidates.sorters.availability_status') },
                    ]}
                    onChange={(sort) => onSortChange?.(sort)}
                  />
                )
              }

              {
                [TenantType.Recruiter, TenantType.Client].includes(tenantType) && (
                  <Box>
                    <Button
                      size="medium"
                      onClick={navigateToAdvancedSearch}
                      startIcon={<Iconify icon="eva:search-fill" />}
                      variant="outlined"
                      sx={{ textWrap: 'nowrap', width: '100%' }}
                    >
                      {t('candidates.advanced_search')}
                    </Button>
                  </Box>
                )
              }

              <Box>
                <Button
                  size="medium"
                  onClick={actionsControl.onOpen}
                  startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
                  variant="outlined"
                  disabled={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)}
                  sx={{ textWrap: 'nowrap' }}
                >
                  {t('common.actions')}
                </Button>
              </Box>
            </Box>

          </Stack>

          {
            selectedLabels.length > 0 && (
              <Stack direction="column" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('candidates.filters.selected_labels')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {selectedLabels.map((label) => (
                    <Chip
                      key={label.value}
                      label={label.label}
                      onDelete={() => {
                        const newLabels = selectedLabels.filter((l) => l.value !== label.value);
                        setSelectedLabels(newLabels);
                        onFilters('labels', newLabels.map((l) => l.value));
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )
          }

          {
            selectedSkills.length > 0 && (
              <Stack direction="column" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('candidates.filters.selected_skills')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {selectedSkills.map((label) => (
                    <Chip
                      key={label.value}
                      label={label.value}
                      onDelete={() => {
                        const newSkills = selectedSkills.filter((l) => l.value !== label.value);
                        setSelectedSkills(newSkills);
                        onFilters('skills', newSkills.map((l) => l.value));
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )
          }

        </Stack>
      )}

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >
        {([TenantType.Recruiter].includes(tenantType) && onInviteCandidates) && (
          <MenuItem
            onClick={() => {
              actionsControl.onClose();
              onInviteCandidates?.();
            }}
          >
            <Iconify icon="lucide:mail-plus" />
            {t('candidates.invites.invite_button_text')}
          </MenuItem>
        )}

        {([TenantType.Recruiter, TenantType.Client].includes(tenantType) && onCreateCandidate) && (
          <Tooltip title={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT) ? t('billing.feature_locked_tooltip') : ''} sx={{ cursor: 'pointer' }}>
            <span>
              <MenuItem
                disabled={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)}
                onClick={() => {
                  actionsControl.onClose();
                  onCreateCandidate?.();
                }}
              >
                <Iconify icon="material-symbols:add" />
                {t('candidates.create_candidate')}
              </MenuItem>
            </span>
          </Tooltip>
        )}

        {([TenantType.Recruiter, TenantType.Client].includes(tenantType) && onImportCandidate) && (
          <Tooltip title={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT) ? t('billing.feature_locked_tooltip') : ''} sx={{ cursor: 'pointer' }}>
            <span>
              <MenuItem
                disabled={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)}
                onClick={() => {
                  actionsControl.onClose();
                  onImportCandidate?.();
                }}
              >
                <Iconify icon="grommet-icons:document-user" />
                {t('candidates.import_candidate')}
              </MenuItem>
            </span>
          </Tooltip>
        )}

        {onBulkImport && (
          <MenuItem
            onClick={() => {
              actionsControl.onClose();
              onBulkImport?.();
            }}
          >
            <Iconify icon="stash:file-import-duotone" />
            {t(`candidates.bulk_import`)}
          </MenuItem>
        )}

        {
          onBulkAddToJob && (
            <Tooltip title={bulkAddDisabledTooltip}>
              <span>
                <MenuItem
                  disabled={isBulkAddDisabled}
                  onClick={() => {
                    actionsControl.onClose();
                    onBulkAddToJob?.(table?.selected ?? []);
                  }}
                >
                  <Iconify icon="mdi:invite" />
                  {t('candidates.add_bulk')}
                </MenuItem>
              </span>
            </Tooltip>
          )
        }

        {
          onAddToList && (
            <Tooltip title={!table?.selected.length ? t('lists.bulk_add_disabled_tootip') : ''}>
              <span>
                <MenuItem
                  disabled={!table?.selected.length}
                  onClick={() => {
                    actionsControl.onClose();
                    onAddToList?.(table?.selected ?? []);
                  }}
                >
                  <Iconify icon="material-symbols:contextual-token-add-outline" />
                  {t('lists.add_to_list')}
                </MenuItem>
              </span>
            </Tooltip>
          )
        }

        {
          (onSendMultiEmail && [TenantType.Client, TenantType.Recruiter].includes(tenantType)) && (
            <Tooltip title={!table?.selected.length ? t('contacts.send_bulk_mail_tooltip') : ''}>
              <span>
                <MenuItem
                  disabled={!table?.selected.length}
                  onClick={() => {
                    actionsControl.onClose();
                    onSendMultiEmail?.(table?.selected ?? []);
                  }}
                >
                  <Iconify icon="iconoir:send-mail" />
                  {t('contacts.send_bulk_mail')}
                </MenuItem>
              </span>
            </Tooltip>
          )
        }

      </CustomPopover>
    </Stack>
  );
}